export type MessagePopupLayout = "floating" | "drawer";

export const NO_CONTACT_METHODS_REASON =
  "Add an email or phone number to start a conversation.";

/**
 * Which layout the user last chose. Namespaced like the inbox's own prefs
 * (`inbox.activityPanelOpen`) and written through `writeLocalStorage` so the
 * header menu and the pop-up agree inside the same tab.
 */
export const MESSAGE_POPUP_LAYOUT_KEY = "messagePopup.layout";

const DEFAULT_MESSAGE_POPUP_LAYOUT: MessagePopupLayout = "floating";

export const readStoredLayout = (): MessagePopupLayout => {
  const stored = localStorage.getItem(MESSAGE_POPUP_LAYOUT_KEY);
  return stored === "drawer" || stored === "floating"
    ? stored
    : DEFAULT_MESSAGE_POPUP_LAYOUT;
};

/**
 * Floating card — 400x600 in the bottom-right corner, the same recipe as the
 * in-call card (softphone/CallScreen.tsx): a fixed corner anchor, a width capped
 * to the viewport, and the `rounded-2xl bg-white shadow-2xl ring-1` surface.
 * Full-screen below `md`, where the onboarding checklist owns the bottom edge.
 */
export const MESSAGE_POPUP_CARD_CLASS =
  "fixed inset-0 flex flex-col overflow-hidden bg-white md:inset-auto md:bottom-4 md:right-4 md:h-[600px] md:max-h-[calc(100vh-2rem)] md:w-full md:max-w-[400px] md:rounded-2xl md:shadow-2xl md:ring-1 md:ring-alpha-100";

/**
 * Drawer — 500px wide, full height, right edge, and deliberately no backdrop:
 * the page behind it stays scrollable and clickable ("Don't block users when
 * the chat is open"). Hand-rolled rather than react-modern-drawer, which always
 * renders its panel at zIndex + 1 and would break the stacking contract.
 */
export const MESSAGE_POPUP_DRAWER_CLASS =
  "fixed inset-0 flex flex-col overflow-hidden bg-white md:inset-y-0 md:left-auto md:right-0 md:w-full md:max-w-[500px] md:rounded-l-[8px] md:border-l md:border-alpha-100 md:shadow-[-16px_0_21px_-4px_rgba(28,29,34,0.08)]";

export const MESSAGE_POPUP_EMPTY_DESCRIPTION =
  "Send a message to start the conversation. Messages are also delivered to the applicant via SMS.";

// SMS / Email empty threads — no SMS-mirroring promise there. Placeholder copy
// pending the designer's wording (design feedback 2026-09-02 asked for a
// paragraph under every empty state).
export const MESSAGE_POPUP_EMPTY_DESCRIPTION_GENERIC =
  "Send a message to start the conversation.";

// Chat messages are mirrored to SMS by the backend since the native-chat
// migration, so the pop-up says so under the composer.
export const MESSAGE_POPUP_SMS_NOTE = "All chat messages are also sent via SMS";
